import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jobsService, WorkflowJob } from '../services/jobsService';
import { useAuth } from '../contexts/AuthContext';
import { BETA_TOOL_LABELS } from '../constants/betaToolLabels';

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  ...BETA_TOOL_LABELS,
  loss_run_analysis: 'Loss Run Analysis',
  loss_run_analyzer: 'Loss Run Analyzer',
  loss_run_analyzer_v4: 'Loss Run Analyzer',
  renewal_analysis: 'Renewal Analysis',
  year_over_year: 'Year Over Year',
  yoy_renewal: 'YOY Renewal',
  personal_lines_yoy_renewal: 'Personal Lines YOY',
  commercial_lines_exec_renewal: 'Commercial Lines Exec',
  coverage_analysis: 'Coverage Analysis',
  insurance_requirements: 'Insurance Requirements',
  document_comparison: 'Document Comparison',
  research: 'Research',
  policy_comparison: 'Policy Comparison',
  financial_analysis: 'Financial Analysis',
  client_renewal_presentation: 'Client Renewal Presentation',
  certificate_of_effort: 'Certificate of Effort',
  rate_analysis: 'Rate Analysis',
  portfolio_analyzer: 'Portfolio Analyzer',
  homeowners_comparison: 'Homeowners Comparison',
  personal_umbrella: 'Personal Umbrella',
};

const labelFor = (t: string) => WORKFLOW_TYPE_LABELS[t] || t.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const relativeTime = (iso: string | null): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const StatusPill: React.FC<{ status: WorkflowJob['status'] }> = ({ status }) => {
  const styles =
    status === 'completed'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      : status === 'processing' || status === 'pending'
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      : status === 'failed' || status === 'dead_letter'
      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  const label =
    status === 'dead_letter' ? 'failed' : status === 'processing' ? 'running' : status;
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full font-medium ${styles}`}>
      {label}
    </span>
  );
};

interface RecentActivityPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Slide-out drawer rendering the user's recent workflow runs. Mounted at the
 * app root and toggled from the header. Closed by default.
 */
const RecentActivityPanel: React.FC<RecentActivityPanelProps> = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<WorkflowJob[]>([]);
  const [loading, setLoading] = useState(true);

  // Refresh on open so users see fresh state without a full reload.
  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await jobsService.getUserJobs(token, true);
        if (cancelled) return;
        if (res.success) {
          const sorted = [...res.jobs].sort((a, b) => {
            const at = new Date(a.completedAt || a.createdAt).getTime();
            const bt = new Date(b.completedAt || b.createdAt).getTime();
            return bt - at;
          });
          setJobs(sorted.slice(0, 15));
        }
      } catch {
        // best-effort widget
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, token]);

  // Close on Esc
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Recent activity"
        aria-hidden={!isOpen}
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-dark-surface border-l border-gray-200 dark:border-dark-border shadow-2xl z-50 transform transition-transform duration-200 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } flex flex-col`}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-dark-border">
          <div>
            <h2 className="text-base font-semibold text-secondary dark:text-dark-text">
              Recent activity
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Your latest workflow runs
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close recent activity"
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-sm text-gray-500 dark:text-gray-400">
              Loading…
            </div>
          ) : jobs.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No recent workflow runs yet. Start a workflow and it'll show up here.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobs.map((j) => {
                const canOpen = j.status === 'completed' && j.savedReportId;
                const customerName = j.customer?.name || 'Unknown customer';
                return (
                  <li
                    key={j.id}
                    className={`px-5 py-3 ${canOpen ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40' : ''}`}
                    onClick={() => {
                      if (canOpen) {
                        navigate(`/workflow-report?reportId=${j.savedReportId}`);
                        onClose();
                      }
                    }}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-sm font-medium text-secondary dark:text-dark-text truncate">
                        {labelFor(j.workflowType)}
                      </span>
                      <StatusPill status={j.status} />
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {customerName} · {relativeTime(j.completedAt || j.createdAt)}
                    </div>
                    {canOpen && (
                      <Link
                        to={`/workflow-report?reportId=${j.savedReportId}`}
                        onClick={(e) => { e.stopPropagation(); onClose(); }}
                        className="inline-block mt-1 text-xs font-medium text-primary hover:text-blue-700 dark:hover:text-blue-400"
                      >
                        Open report →
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
};

export default RecentActivityPanel;
